"use client";

import { useTranslations } from "next-intl";
import { Section } from "@/components/ui/Section";
import { Marquee } from "@/components/ui/Marquee";
import Image from "next/image";

interface Partner {
  name: string;
  image: string;
  link: string | null;
  imageDark?: string;
}

const partners: Partner[] = [
  {
    name: "DJ Kondzio",
    image: "/images/partners/djkondzio.png",
    imageDark: "/images/partners/djkondzio-dark.png",
    link: "//www.djkondzio.com/",
  },
  {
    name: "EnergyParty",
    image: "/images/partners/energyparty.png",
    imageDark: "/images/partners/energyparty-dark.png",
    link: null,
  },
  {
    name: "xFiveRecords",
    image: "/images/partners/xfiverecords.png",
    imageDark: "/images/partners/xfiverecords-dark.png",
    link: null,
  },
  {
    name: "SmartLinks",
    image: "/images/partners/smartlinks.png",
    imageDark: "/images/partners/smartlinks-dark.png",
    link: null,
  },
  {
    name: "BombArts",
    image: "/images/partners/bombarts.png",
    link: "//facebook.com/bombarts/",
  },
  {
    name: "home.pl",
    image: "/images/partners/home.png",
    imageDark: "/images/partners/home-dark.png",
    link: "//home.pl/",
  },
  {
    name: "OVH",
    image: "/images/partners/ovh.png",
    imageDark: "/images/partners/ovh-dark.png",
    link: "//ovh.pl/",
  },
  {
    name: "Hostgier",
    image: "/images/partners/hostgier.png",
    imageDark: "/images/partners/hostgier-dark.png",
    link: "//hostgier.pl/",
  },
  {
    name: "CyberSecurity",
    image: "/images/partners/cybersecurity.png",
    link: "https://waf.devdj.pl/",
  },
  {
    name: "CloudFlare",
    image: "/images/partners/cloudflare.png",
    imageDark: "/images/partners/cloudflare-dark.png",
    link: "//cloudflare.com/",
  },
  {
    name: "TeamSpeak",
    image: "/images/partners/teamspeak.png",
    link: "//teamspeak.com/",
  },
  {
    name: "Thunder Music Festival",
    image: "/images/partners/thundermusicfestival.png",
    imageDark: "/images/partners/thundermusicfestival-dark.png",
    link: null,
  },
  {
    name: "Thunder Worldwide",
    image: "/images/partners/thunderworldwide.png",
    imageDark: "/images/partners/thunderworldwide-dark.png",
    link: null,
  },
  {
    name: "CrowdQuant",
    image: "/images/partners/crowdquant.png",
    imageDark: "/images/partners/crowdquant-dark.png",
    link: null,
  },
];

export function Partners() {
  const t = useTranslations("Index");

  // Partners list passed directly to Marquee (which duplicates 4x)

  return (
    <Section
      id="partners"
      title={t("partners_title")}
      subtitle={t("partners_subtitle")}
      className="bg-(--bg-card) border-t border-(--border-subtle)"
      separator={{
        position: "bottom",
        variant: "single-wave",
        className: "rotate-x-[180deg]",
        fillClassName: "fill-[var(--bg-primary)]",
      }}
    >
      <div className="relative w-full max-w-7xl mx-auto">
        {/* Gradient Fade Masks */}
        <div className="absolute inset-y-0 left-0 w-24 bg-linear-to-r from-(--bg-card) to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-24 bg-linear-to-l from-(--bg-card) to-transparent z-10 pointer-events-none" />

        {/* Marquee Container */}
        <div
          className="relative w-full overflow-hidden select-none"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
          }}
        >
          {/* Using shared Marquee component */}
          {/* Note: Marquee duplicates children 4x. We pass the partners list once. */}
          <Marquee
            velocity={0.3} // Standard slow speed
            pauseOnHover={false} // Explicitly disabled
            className="py-4 [--gap:3rem] md:[--gap:5rem]"
          >
            {partners.map((partner, index) => {
              return (
                <a
                  key={`${partner.name}-${index}`}
                  href={partner.link || "#"}
                  target={partner.link ? "_blank" : "_self"}
                  rel={partner.link ? "noopener noreferrer" : ""}
                  className={`relative shrink-0 w-36 h-16 md:w-48 md:h-20 transition-all duration-300
                    ${!partner.link ? "cursor-default" : "cursor-pointer"}
                  `}
                  // Prevent drag issues
                  draggable={false}
                >
                  <div className="relative w-full h-full px-2">
                    {partner.imageDark ? (
                      <>
                        {/* Light Mode Image (with Dark Mode override available) */}
                        <Image
                          src={partner.image}
                          alt={`${partner.name} (Light)`}
                          fill
                          className="object-contain transition-all duration-300 dark:hidden grayscale opacity-70 hover:grayscale-0 hover:opacity-100"
                          draggable={false}
                        />
                        {/* Dark Mode Image */}
                        <Image
                          src={partner.imageDark}
                          alt={`${partner.name} (Dark)`}
                          fill
                          className="object-contain transition-all duration-300 hidden dark:block grayscale opacity-70 hover:grayscale-0 hover:opacity-100"
                          draggable={false}
                        />
                      </>
                    ) : (
                      /* Single Image Fallback */
                      <Image
                        src={partner.image}
                        alt={partner.name}
                        fill
                        className={`
                          object-contain transition-all duration-300
                          opacity-70 grayscale
                          hover:grayscale-0 hover:opacity-100
                          
                          /* Dark Mode specific adjustments for single image */
                          dark:opacity-80
                        `}
                        draggable={false}
                      />
                    )}
                  </div>
                </a>
              );
            })}
          </Marquee>
        </div>
      </div>
    </Section>
  );
}
